import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { nextScrollAttempt } from './scrollMath';
import { readEntry, sessionStore, writeEntry } from './viewStore';

const BUCKET = 'onvice.scroll.v1';
const MAX_WAIT_MS = 1500;
const USER_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;

function historyKey(): string | null {
  const st: unknown = window.history.state;
  return st && typeof st === 'object' && typeof (st as { key?: unknown }).key === 'string' ? (st as { key: string }).key : null;
}

export interface UseScrollRestoreOptions {
  enabled?: boolean;
}

/**
 * Stellt die Scrollposition einer Liste wieder her, sobald sie fertig geladen ist (Regel
 * „Dokument-Navigation" R2). Die Wiederherstellung des Routers läuft vor dem Laden — die
 * Seite ist dann zu kurz und landet oben.
 *
 * `ready` = Daten der Liste sind da. Wiederhergestellt wird nur bei Vor/Zurück; ein
 * `?scrollTo=` in der Adresse hat Vorrang. Greift der Anwender währenddessen selbst ein,
 * bricht die Wiederherstellung ab.
 */
export function useScrollRestore(ready: boolean, opts: UseScrollRestoreOptions = {}): void {
  const enabled = opts.enabled !== false;
  const location = useLocation();
  const navType = useNavigationType();
  const key = location.key;
  const restoring = useRef(false);
  const restoredFor = useRef<string | null>(null);

  // Merken. Als Layout-Effekt, damit der Zuhörer beim Seitenwechsel weg ist, bevor der
  // Router die neue Seite nach oben scrollt.
  useLayoutEffect(() => {
    if (!enabled || !ready) return;
    const store = sessionStore();
    if (!store) return;
    let frame = 0;
    const remember = () => {
      frame = 0;
      if (restoring.current) return;
      // Das Ereignis gehört schon zur nächsten Seite.
      const hk = historyKey();
      if (hk != null && hk !== key) return;
      writeEntry(store, BUCKET, key, Math.round(window.scrollY));
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(remember); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, ready, key]);

  // Wiederherstellen.
  useEffect(() => {
    if (!enabled || !ready || restoredFor.current === key) return;
    restoredFor.current = key;
    const store = sessionStore();
    if (!store) return;
    const hasAnchor = new URLSearchParams(location.search).has('scrollTo');
    const target = navType === 'POP' && !hasAnchor ? readEntry<number>(store, BUCKET, key) : undefined;
    if (typeof target !== 'number' || target <= 0) {
      // Filterwechsel erzeugt einen neuen Verlaufsschlüssel: aktuelle Position darunter merken.
      if (navType !== 'PUSH') writeEntry(store, BUCKET, key, Math.round(window.scrollY));
      return;
    }

    restoring.current = true;
    const started = Date.now();
    let timer = 0;
    const stop = () => {
      restoring.current = false;
      if (timer) window.clearTimeout(timer);
      for (const ev of USER_EVENTS) window.removeEventListener(ev, stop);
    };
    const attempt = () => {
      const a = nextScrollAttempt(target, document.documentElement.scrollHeight, window.innerHeight);
      window.scrollTo(0, a.y);
      if (a.done || Date.now() - started > MAX_WAIT_MS) { stop(); return; }
      timer = window.setTimeout(attempt, 60);
    };
    for (const ev of USER_EVENTS) window.addEventListener(ev, stop, { passive: true });
    attempt();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ready, key]);
}
